import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectLatestRoute, selectLoadingLatestRoute } from '../../../features/tripcenter/routeSlice';
import { setWorkingNotifs, selectWorkingNotifs } from '../../../features/tripcenter/googleRouteMakerSlice';
import PropTypes from 'prop-types';

// MUI components
import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    Button,
    IconButton,
    Divider,
    CircularProgress,
    Alert,
    Paper,
    Tabs,
    Tab,
    Chip,
    Card,
    CardContent,
    Tooltip,
    Fade,
    Grid
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

// Define notification channel configuration with icons and colors
const notificationChannels = {
    telegram: {
        icon: <TelegramIcon />,
        color: '#0088cc',
        label: 'Telegram',
        fieldType: 'number'
    },
    whatsApp: {
        icon: <WhatsAppIcon />,
        color: '#25D366',
        label: 'WhatsApp',
        fieldType: 'number'
    },
    email: {
        icon: <EmailIcon />,
        color: '#DB4437',
        label: 'Email',
        fieldType: 'email'
    }
};

const RouteNotifications = ({ initialNotifications }) => {
    const dispatch = useDispatch();
    const latestRoute = useSelector(selectLatestRoute);
    const loadingLatestRoute = useSelector(selectLoadingLatestRoute);
    const workingNotifs = useSelector(selectWorkingNotifs);

    // Active tab state
    const [activeTab, setActiveTab] = useState('telegram');

    // Edit mode tracking
    const [editingRow, setEditingRow] = useState(null);

    // Initialize notifications state - filter out push and SMS
    const [notifications, setNotifications] = useState(() => {
        const initialState = {
            telegram: [],
            whatsApp: [],
            email: [],
            ...workingNotifs
        };
        delete initialState.push;
        delete initialState.sms;
        return initialState;
    });

    // Effect to handle initial notifications setup
    useEffect(() => {
        if (initialNotifications) {
            const filteredNotifications = { ...initialNotifications };
            delete filteredNotifications.push;
            delete filteredNotifications.sms;

            const newNotifications = {
                ...filteredNotifications,
                telegram: filteredNotifications.telegram || [],
                whatsApp: filteredNotifications.whatsApp || [],
                email: filteredNotifications.email || []
            };

            setNotifications(newNotifications);
            dispatch(setWorkingNotifs(newNotifications));
        } else if (!loadingLatestRoute && latestRoute?.notifications) {
            const filteredNotifications = { ...latestRoute.notifications };
            delete filteredNotifications.push;
            delete filteredNotifications.sms;

            const newNotifications = {
                ...filteredNotifications,
                telegram: filteredNotifications.telegram || [],
                whatsApp: filteredNotifications.whatsApp || [],
                email: filteredNotifications.email || []
            };

            setNotifications(newNotifications);
            dispatch(setWorkingNotifs(newNotifications));
        }
    }, [initialNotifications, latestRoute, loadingLatestRoute, dispatch]);

    // Effect to update Redux when notifications change
    useEffect(() => {
        dispatch(setWorkingNotifs(notifications));
    }, [notifications, dispatch]);

    // Modify handlers to only update local state
    const handleAddRow = (type) => {
        const newEntry = {
            name: '',
            [type === 'email' ? 'email' : 'number']: '',
            categories: []
        };

        setNotifications(prev => ({
            ...prev,
            [type]: [...prev[type], newEntry]
        }));

        // Set to edit mode for the new entry
        setEditingRow(`${type}-${notifications[type].length}`);
    };

    const handleRemoveRow = (type, index) => {
        setNotifications(prev => ({
            ...prev,
            [type]: prev[type].filter((_, i) => i !== index)
        }));

        if (editingRow === `${type}-${index}`) {
            setEditingRow(null);
        }
    };

    const handleChange = (type, index, field, value) => {
        setValidationErrors(prev => ({
            ...prev,
            [`${type}-${index}-${field}`]: ''
        }));

        if (field === 'number' || field === 'email') {
            let isValid = true;
            let errorMessage = '';

            if (field === 'email') {
                isValid = isValidEmail(value);
                errorMessage = isValid ? '' : 'Invalid email address';
            } else if (field === 'number') {
                isValid = isValidPhone(value);
                errorMessage = isValid ? '' : 'Phone number must be 10 digits';
            }

            setValidationErrors(prev => ({
                ...prev,
                [`${type}-${index}-${field}`]: errorMessage
            }));
        }

        setNotifications(prev => {
            const newNotifications = {
                ...prev,
                [type]: prev[type].map((item, i) =>
                    i === index ? { ...item, [field]: value } : item
                )
            };

            return newNotifications;
        });
    };

    const handleCategoryChange = (type, index, category) => {
        setNotifications(prev => {
            const newNotifications = {
                ...prev,
                [type]: prev[type].map((item, i) => {
                    if (i === index) {
                        let newCategories = [...item.categories];

                        if (category === 'activeStatus') {
                            newCategories = !item.categories.includes(category)
                                ? ['activeStatus', 'ruleViolation', 'tripStage', 'finalReport']
                                : [];
                        } else {
                            if (item.categories.includes(category)) {
                                newCategories = newCategories.filter(c => c !== category && c !== 'activeStatus');
                            } else {
                                newCategories.push(category);
                                const allSubCategories = Object.keys(categoryOptions.sub);
                                if (allSubCategories.every(subCat =>
                                    newCategories.includes(subCat) || subCat === category)) {
                                    newCategories.push('activeStatus');
                                }
                            }
                        }

                        return { ...item, categories: newCategories };
                    }
                    return item;
                })
            };

            return newNotifications;
        });
    };

    // Handle edit toggle
    const toggleEditMode = (type, index) => {
        const rowId = `${type}-${index}`;
        setEditingRow(editingRow === rowId ? null : rowId);
    };

    // Define category options
    const categoryOptions = {
        main: {
            activeStatus: 'Everything'
        },
        sub: {
            ruleViolation: 'Rules',
            tripStage: 'Overview',
            finalReport: 'Final Report'
        }
    };

    // Add validation helper functions
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidPhone = (phone) => {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phone);
    };

    // Add state for validation errors
    const [validationErrors, setValidationErrors] = useState({});

    // Add a function to check if a notification row is complete
    const isNotificationComplete = (notification, type) => {
        const hasName = !!notification.name?.trim();
        const hasContact = type === 'email'
            ? isValidEmail(notification.email)
            : isValidPhone(notification.number);
        const hasCategories = notification.categories?.length > 0;

        return hasName && hasContact && hasCategories;
    };

    // Get category display name
    const getCategoryLabel = (category) => {
        return (
            categoryOptions.main[category] ||
            categoryOptions.sub[category] ||
            category.charAt(0).toUpperCase() + category.slice(1)
        );
    };

    if (loadingLatestRoute) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2 }}>Loading Notifications data...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            p: 3,
            maxWidth: '1000px',
            margin: '0 auto'
        }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.dark' }}>
                    Configure Notifications
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Set up notifications to keep stakeholders updated about trip events. You can configure different types of notifications for each channel.
                </Typography>
                <Divider sx={{ mt: 2 }} />
            </Box>

            {/* Tabs for notification channels */}
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 3
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    variant="fullWidth"
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            py: 2
                        }
                    }}
                >
                    {Object.entries(notificationChannels).map(([key, { icon, label, color }]) => (
                        <Tab
                            key={key}
                            value={key}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ color }}>
                                        {icon}
                                    </Box>
                                    <Typography variant="button">{label}</Typography>
                                    <Chip
                                        size="small"
                                        label={notifications[key]?.length || 0}
                                        sx={{
                                            height: 20,
                                            '& .MuiChip-label': { px: 1 },
                                            bgcolor: notifications[key]?.length ? 'primary.light' : 'grey.300',
                                            color: notifications[key]?.length ? 'white' : 'text.secondary'
                                        }}
                                    />
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                {/* Notification content for active tab */}
                <Box sx={{ p: 3 }}>
                    {/* Channel description and add button */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            {activeTab === 'telegram' && 'Send notifications via Telegram. Recipients need to be registered with our bot.'}
                            {activeTab === 'whatsApp' && 'Send notifications via WhatsApp. Messages will be sent from our system number.'}
                            {activeTab === 'email' && 'Send email notifications to specified addresses.'}
                        </Typography>

                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<AddIcon />}
                            onClick={() => handleAddRow(activeTab)}
                            sx={{
                                borderRadius: 2,
                                px: 2
                            }}
                        >
                            Add Recipient
                        </Button>
                    </Box>

                    {/* Empty state */}
                    {(!notifications[activeTab] || notifications[activeTab].length === 0) && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                borderRadius: 2,
                                bgcolor: 'grey.50',
                                textAlign: 'center',
                                border: '1px dashed',
                                borderColor: 'divider'
                            }}
                        >
                            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No {notificationChannels[activeTab].label} Notifications
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Add recipients to send them notifications about trip events.
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddRow(activeTab)}
                                sx={{ mt: 1, borderRadius: 2 }}
                            >
                                Add First Recipient
                            </Button>
                        </Paper>
                    )}

                    {/* Notification list */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        {notifications[activeTab]?.map((recipient, index) => {
                            const isEditing = editingRow === `${activeTab}-${index}`;
                            const isComplete = isNotificationComplete(recipient, activeTab);
                            const fieldType = notificationChannels[activeTab].fieldType;

                            return (
                                <Card
                                    key={index}
                                    elevation={0}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        borderColor: isEditing
                                            ? 'primary.main'
                                            : !isComplete
                                                ? 'warning.light'
                                                : 'divider',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        overflow: 'visible'
                                    }}
                                >
                                    {!isComplete && !isEditing && (
                                        <Chip
                                            size="small"
                                            label="Incomplete"
                                            color="warning"
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: 12,
                                                zIndex: 1
                                            }}
                                        />
                                    )}

                                    <CardContent sx={{ p: 0 }}>
                                        {isEditing ? (
                                            /* Edit Mode */
                                            <Box sx={{ p: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label="Name"
                                                            value={recipient.name || ''}
                                                            onChange={(e) => handleChange(activeTab, index, 'name', e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            placeholder="Enter recipient name"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={8}>
                                                        <TextField
                                                            label={fieldType === 'email' ? 'Email Address' : 'Phone Number'}
                                                            value={recipient[fieldType] || ''}
                                                            onChange={(e) => handleChange(activeTab, index, fieldType, e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            error={!!validationErrors[`${activeTab}-${index}-${fieldType}`]}
                                                            helperText={validationErrors[`${activeTab}-${index}-${fieldType}`]}
                                                            placeholder={fieldType === 'email' ? 'example@domain.com' : '10-digit number'}
                                                            inputProps={{
                                                                inputMode: fieldType === 'email' ? 'email' : 'numeric',
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>

                                                <Divider sx={{ my: 2 }} />

                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                                    Notification Types
                                                </Typography>

                                                <Box sx={{ mb: 1 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={recipient.categories.includes('activeStatus')}
                                                                onChange={() => handleCategoryChange(activeTab, index, 'activeStatus')}
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {categoryOptions.main.activeStatus}
                                                            </Typography>
                                                        }
                                                    />
                                                </Box>

                                                <Box sx={{
                                                    display: 'flex',
                                                    gap: 2,
                                                    flexWrap: 'wrap',
                                                    ml: 3
                                                }}>
                                                    {Object.entries(categoryOptions.sub).map(([value, label]) => (
                                                        <FormControlLabel
                                                            key={value}
                                                            control={
                                                                <Checkbox
                                                                    checked={recipient.categories.includes(value)}
                                                                    onChange={() => handleCategoryChange(activeTab, index, value)}
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={recipient.categories.includes('activeStatus')}
                                                                />
                                                            }
                                                            label={<Typography variant="body2">{label}</Typography>}
                                                        />
                                                    ))}
                                                </Box>

                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    gap: 1,
                                                    mt: 2
                                                }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => setEditingRow(null)}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        Done
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() => handleRemoveRow(activeTab, index)}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ) : (
                                            /* View Mode */
                                            <Box sx={{ 
                                                    p: 2,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}>
                                                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                                        {/* Icon with channel color */}
                                                        <Box sx={{
                                                            color: notificationChannels[activeTab].color,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '50%',
                                                            bgcolor: `${notificationChannels[activeTab].color}10`
                                                        }}>
                                                            {notificationChannels[activeTab].icon}
                                                        </Box>

                                                        {/* Recipient info */}
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight={500}>
                                                                {recipient.name || '(No name)'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {recipient[fieldType] || '(No contact info)'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        {/* Category chips */}
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            {recipient.categories.includes('activeStatus') ? (
                                                                <Chip
                                                                    size="small"
                                                                    label="All Notifications"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    icon={<CheckCircleIcon />}
                                                                />
                                                            ) : (
                                                                recipient.categories.length > 0 ? (
                                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                        {recipient.categories.map(category => (
                                                                            <Chip
                                                                                key={category}
                                                                                size="small"
                                                                                label={getCategoryLabel(category)}
                                                                                variant="outlined"
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                ) : (
                                                                    <Chip
                                                                        size="small"
                                                                        label="No notifications selected"
                                                                        color="warning"
                                                                        variant="outlined"
                                                                        icon={<ErrorIcon />}
                                                                    />
                                                                )
                                                            )}
                                                        </Box>

                                                        {/* Action buttons */}
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Tooltip title="Edit">
                                                                <IconButton 
                                                                    size="small"
                                                                    onClick={() => toggleEditMode(activeTab, index)}
                                                                    sx={{
                                                                        color: 'primary.main',
                                                                        '&:hover': { bgcolor: 'primary.50' }
                                                                    }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleRemoveRow(activeTab, index)}
                                                                    sx={{
                                                                        color: 'error.main',
                                                                        '&:hover': { bgcolor: 'error.50' }
                                                                    }}
                                                                >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

// Update PropTypes
RouteNotifications.propTypes = {
    initialNotifications: PropTypes.object
};

export default RouteNotifications; 